import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeTokenById } from '@/api/modules'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import TokenInfoSelect from '@/common/TokenInfoSelect'
import TokenSelect from '@/common/TokenSelect'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildRemoveTokenInfoMessage,
  RemoveTokenInfoFormValues,
} from './tokenInfoMessageBuilder'

const removeTokenInfoSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  selectedTokenId: z.string().optional(),
  selectedChainId: z.string().optional(),
  tokenId: z
    .string()
    .min(1, 'Token ID is required')
    .refine(
      async val => {
        try {
          await fetchBridgeTokenById(val)
          return true
        } catch {
          return false
        }
      },
      { message: 'Token with this ID does not exist' },
    ),
  chainId: z.string().min(1, 'Chain ID is required'),
})

export default function RemoveTokenInfoForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<RemoveTokenInfoFormValues>({
    resolver: zodResolver(removeTokenInfoSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      selectedTokenId: '',
      selectedChainId: '',
      tokenId: '',
      chainId: '',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const selectToken = (tokenId: string) => {
    form.setValue('tokenId', tokenId, { shouldValidate: true })
  }

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildRemoveTokenInfoMessage(
        watchedValues as RemoveTokenInfoFormValues,
      )
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Remove Token Info Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to remove token information from a specific chain
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>
            Token Info Identification
          </h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel>Select Token</UiLabel>
              <TokenSelect<RemoveTokenInfoFormValues>
                control={form.control}
                name='selectedTokenId'
                onValueChange={tokenId => selectToken(tokenId)}
                disabled={isSubmitting}
                placeholder='Select a token'
              />
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='tokenId'>Token ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='tokenId'
                  {...form.register('tokenId')}
                  placeholder='1'
                  disabled={isSubmitting}
                  readOnly
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.tokenId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.tokenId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel>Select Chain</UiLabel>
              <TokenInfoSelect<RemoveTokenInfoFormValues>
                control={form.control}
                name='selectedChainId'
                tokenId={form.watch('tokenId')}
                mode='remove'
                onValueChange={(chainId: string) =>
                  form.setValue('chainId', chainId, { shouldValidate: true })
                }
                disabled={isSubmitting}
                placeholder='Select a chain'
              />
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='chainId'>Chain ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='chainId'
                  {...form.register('chainId')}
                  placeholder='ethereum'
                  disabled={isSubmitting}
                  readOnly
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.chainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.chainId.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'remove-token-info-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildRemoveTokenInfoMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
